import { MIN_REFRESH_DELAY_MS, REFRESH_LEAD_MS } from './config';
import { authEnvironment, type AuthEnvironment } from './environment';
import { decodeExpMs } from './jwt';

export interface RefreshSchedulerOptions {
    /** Текущий access — проверка срока при возврате на вкладку. */
    getAccessToken: () => string | null;
    /** Ротация: single-flight во вкладке и Web Lock между вкладками — забота оркестратора. Отказ глотается. */
    refresh: () => Promise<unknown>;
    env?: Partial<Pick<AuthEnvironment, 'now' | 'setTimer' | 'visibility'>>;
}

export interface RefreshScheduler {
    /** Слушать возврат на вкладку; повторный вызов — no-op. До `start()` и после `stop()` таймеры не ставятся. */
    start(): void;
    /** Снять таймер и слушателя видимости. */
    stop(): void;
    /**
     * Ротация за `REFRESH_LEAD_MS` до `exp` токена (короткоживущего — на половине оставшегося срока), не раньше
     * `MIN_REFRESH_DELAY_MS`; токен без `exp` — без таймера.
     */
    schedule(accessToken: string): void;
    cancel(): void;
}

/**
 * Упреждающая ротация access: за `REFRESH_LEAD_MS` до истечения и при возврате на вкладку, если до истечения меньше
 * упреждения (таймеры фоновой вкладки браузер замедляет). Срок токена клиент знает только по `exp`, поэтому упреждение
 * считается от оставшегося срока при планировании: access, живущий меньше упреждения, иначе ротировался бы сразу после
 * ротации.
 */
export function createRefreshScheduler({ getAccessToken, refresh, env }: RefreshSchedulerOptions): RefreshScheduler {
    const resolved = authEnvironment(env);
    let offVisibility: (() => void) | null = null;
    let cancelTimer: (() => void) | null = null;
    let lastScheduledToken: string | null = null;
    let scheduledLeadMs = REFRESH_LEAD_MS;

    const runRefresh = () => {
        try {
            refresh().catch(() => undefined);
        } catch {
            // синхронный сбой ротации — как отказ промиса
        }
    };

    const clearTimer = () => {
        cancelTimer?.();
        cancelTimer = null;
    };

    const cancel = () => {
        clearTimer();
        lastScheduledToken = null;
    };

    const onVisibilityChange = () => {
        if (!resolved.visibility.isVisible()) return;
        const token = getAccessToken();
        const expMs = token ? decodeExpMs(token) : null;
        const lead = token === lastScheduledToken ? scheduledLeadMs : REFRESH_LEAD_MS;
        if (expMs !== null && expMs - resolved.now() < lead) runRefresh();
    };

    return {
        start() {
            if (offVisibility) return;
            offVisibility = resolved.visibility.subscribe(onVisibilityChange);
        },
        stop() {
            offVisibility?.();
            offVisibility = null;
            cancel();
        },
        schedule(accessToken) {
            if (!offVisibility) return;
            // Эхо соседней вкладки с тем же токеном не пересчитывает срок и не пересоздаёт таймер.
            if (lastScheduledToken === accessToken && cancelTimer) return;
            clearTimer();
            const expMs = decodeExpMs(accessToken);
            if (!expMs) return;
            const remainingMs = expMs - resolved.now();
            lastScheduledToken = accessToken;
            scheduledLeadMs = Math.min(REFRESH_LEAD_MS, Math.max(0, remainingMs) / 2);
            cancelTimer = resolved.setTimer(runRefresh, Math.max(MIN_REFRESH_DELAY_MS, remainingMs - scheduledLeadMs));
        },
        cancel,
    };
}
